// Module-level cache so getComputedStyle is called at most once per font-size change
// Call setRootFontSizeCache() whenever the root font-size is changed at runtime.
let _cachedRootFontSizePx: number | null = null;

export function setRootFontSizeCache(sizePx: number): void {
  _cachedRootFontSizePx = sizePx;
}

function getRootFontSize(): number {
  if (_cachedRootFontSizePx === null) {
    _cachedRootFontSizePx =
      typeof document !== 'undefined'
        ? parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
        : 16; // SSR fallback matches the CSS default (1080p)
  }
  return _cachedRootFontSizePx;
}

/**
 * Converts a size in rem units to pixels, taking into account the current root font size.
 * If a number is provided, it is treated as a legacy 1080p pixel value (base 16px).
 * @param size The size in rem units or a legacy pixel value.
 * @param defaultSize The default size to use if the conversion fails.
 * @returns The scaled pixel size.
 */
export function remToPixels(size: string | number, defaultSize: number): number {
  const rootFontSize = getRootFontSize();

  if (typeof size === 'number') {
    return Math.round((size / 16) * rootFontSize);
  }

  const trimmedSize = size.trim().toLowerCase();

  if (trimmedSize.endsWith('rem')) {
    const numericPart = parseFloat(trimmedSize.slice(0, -3));
    if (isNaN(numericPart) || numericPart === 0) {
      return defaultSize;
    }
    return Math.round(numericPart * rootFontSize);
  } else {
    const numericPart = parseFloat(trimmedSize);
    if (isNaN(numericPart) || numericPart === 0) {
      return defaultSize;
    }
    return Math.round(numericPart);
  }
}
